import React, { useEffect, useMemo, useState } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import Modal from '../common/modals/Modal';
import { apiGetJson as apiGet, apiPostJson as apiPost } from '../../utils/api';

// Data-driven unit selection (no upload): pull the property's units from the
// processor mirror (property -> buildings -> units), pick a subset manually
// and/or by a % sample, and request them (creates pending tenant files).
export default function UnitSelectionModal({ isOpen, onClose, property, onSelectionComplete }) {
  const processorPropertyId = property?.processor_property_id;
  const [selected, setSelected] = useState(() => new Set());
  const [pct, setPct] = useState(20);

  // Clear any prior selection when the modal opens or the property changes, so
  // stale picks from another property can't carry over.
  useEffect(() => { if (isOpen) setSelected(new Set()); }, [isOpen, processorPropertyId]);

  const { data, isLoading } = useQuery({
    queryKey: ['property-units', processorPropertyId],
    queryFn: () => apiGet(`/admin/audit_tracker/portfolio/properties/${processorPropertyId}/units`),
    enabled: isOpen && !!processorPropertyId,
  });

  const buildings = data?.buildings || [];
  const allUnits = useMemo(() => buildings.flatMap((b) => b.units.map((u) => ({ ...u, building: b.building_name }))), [buildings]);

  const toggle = (id) => setSelected((s) => {
    const n = new Set(s);
    n.has(id) ? n.delete(id) : n.add(id);
    return n;
  });

  // Evenly-spaced sample so it isn't clustered in one building.
  const applySample = () => {
    if (allUnits.length === 0) return;
    const target = Math.max(1, Math.round((pct / 100) * allUnits.length));
    const step = allUnits.length / target;
    const picked = new Set();
    for (let i = 0; i < target; i++) picked.add(allUnits[Math.floor(i * step)].id);
    setSelected(picked);
  };

  const submit = useMutation({
    mutationFn: () => {
      const chosen = allUnits.filter((u) => selected.has(u.id));
      return apiPost(`/admin/audit_tracker/properties/${property.id}/select_units`, {
        selection_method: 'processor_units',
        selection_pct: pct,
        units: chosen.map((u) => ({ unit_number: u.unit_number, building: u.building })),
      });
    },
    onSuccess: (res) => {
      toast.success(`Requested ${res.files_required} units`);
      setSelected(new Set());
      onSelectionComplete?.(res);
      onClose();
    },
    onError: () => toast.error('Could not request units'),
  });

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={`Select units to request: ${property?.property_name || ''}`}>
      <div className="space-y-3">
        <div className="flex items-center gap-2 text-sm">
          <label className="dark:text-gray-200">Sample</label>
          <input type="number" min="1" max="100" value={pct}
            onChange={(e) => { const n = parseInt(e.target.value, 10); if (Number.isFinite(n)) setPct(Math.min(100, Math.max(1, n))); }}
            className="w-16 px-2 py-1 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 dark:text-white" />
          <span className="dark:text-gray-200">%</span>
          <button onClick={applySample} disabled={allUnits.length === 0}
            className="px-2 py-1 bg-blue-600 text-white rounded text-xs hover:bg-blue-700 disabled:opacity-40">Apply sample</button>
          <span className="ml-auto text-gray-500 dark:text-gray-400">{selected.size} of {allUnits.length} selected</span>
        </div>

        <div className="max-h-[55vh] overflow-y-auto border border-gray-200 dark:border-gray-700 rounded">
          {isLoading && <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-500 m-4" />}
          {!isLoading && allUnits.length === 0 && (
            <div className="p-6 text-center text-sm text-gray-500 dark:text-gray-400">No units found in the processor for this property.</div>
          )}
          {buildings.map((b) => (
            <div key={b.building_id}>
              <div className="px-3 py-1.5 bg-gray-100 dark:bg-gray-700 text-xs font-semibold dark:text-gray-200 sticky top-0">
                {b.building_name || 'Building'}{b.date_in_service ? ` · in service ${b.date_in_service}` : ''}
              </div>
              {b.units.map((u) => (
                <label key={u.id} className="flex items-center gap-2 px-3 py-1.5 text-sm border-b border-gray-100 dark:border-gray-800 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800">
                  <input type="checkbox" checked={selected.has(u.id)} onChange={() => toggle(u.id)} />
                  <span className="font-medium dark:text-gray-200 w-16">{u.unit_number || '-'}</span>
                  <span className="text-gray-500 dark:text-gray-400 text-xs">
                    {[u.unit_type, u.beds != null ? `${u.beds}bd` : null, u.sqft ? `${u.sqft}sf` : null, u.bin ? `BIN ${u.bin}` : null].filter(Boolean).join(' · ')}
                  </span>
                  <span className="ml-auto flex gap-1">
                    {Object.entries(u.set_asides || {}).filter(([, v]) => v).map(([k]) => (
                      <span key={k} className="px-1 rounded text-[10px] bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200 uppercase">{k}</span>
                    ))}
                  </span>
                </label>
              ))}
            </div>
          ))}
        </div>

        <div className="flex justify-end gap-2">
          <button onClick={onClose} className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded text-sm dark:text-gray-200">Cancel</button>
          <button onClick={() => submit.mutate()} disabled={selected.size === 0 || submit.isPending}
            className="px-3 py-2 bg-orange-500 text-white rounded text-sm hover:bg-orange-600 disabled:opacity-40">
            Request selected ({selected.size})
          </button>
        </div>
      </div>
    </Modal>
  );
}
